/* eslint-disable camelcase */
import axios from "axios"
import Rails from "@rails/ujs"

const api = axios.create({
  baseURL: window.location.origin,
  headers: { Accept: "application/json" },
  params: { authenticity_token: Rails.csrfToken() },
})

const ok = (data) => {
  data.ok = true
  return data
}

const notOk = (data) => {
  data.ok = false
  return data
}

export const get = async (path, params = {}) => {
  try {
    const response = await api.get(path, { params })
    return ok(response)
  } catch (error) {
    return notOk(error.response)
  }
}

export const post = async (path, payload, params = {}) => {
  try {
    const response = await api.post(path, payload, { params })
    return ok(response)
  } catch (error) {
    return notOk(error.response)
  }
}
export const postFiles = async (path, payload, params = {}) => {
  const headers = { "Content-Type": "multipart/form-data" }
  try {
    const response = await api.post(path, payload, { params, headers })
    return ok(response)
  } catch (error) {
    return notOk(error.response)
  }
}
export const put = async (path, payload, params = {}) => {
  try {
    const response = await api.put(path, payload, { params })
    return ok(response)
  } catch (error) {
    return notOk(error.response)
  }
}
export const destroy = async (path, params = {}) => {
  try {
    const response = await api.delete(path, { params })
    return ok(response)
  } catch (error) {
    return notOk(error.response)
  }
}
export const head = async (path, params = {}) => {
  try {
    const response = await api.head(path, { params })
    return ok(response)
  } catch (error) {
    return notOk(error.response)
  }
}

export const refreshSession = async (refreshSessionPath) => post(refreshSessionPath)

export const createRegularForm = async ({ categoryId }) => post(
  "/api/v1/public-forms",
  { form: { category_id: categoryId } },
)

export const createTaskForm = async ({ categoryId }) => post(
  "/api/v1/task-forms",
  { form: { category_id: categoryId } },
)

export const createSftpRemoteServer = async ({ sftpRemoteServer }) => post(
  "/api/v1/sftp/remote-server",
  { remote_server: sftpRemoteServer },
)

export const updateSftpRemoteServer = async ({ sftpRemoteServer }) => put(
  "/api/v1/sftp/remote-server",
  { remote_server: sftpRemoteServer },
)

export const showSftpRemoteServer = async () => get(
  "/api/v1/sftp/remote-server",
)

export const testConnectionSftpRemoteServer = async () => get(
  "/api/v1/sftp/remote-server/test-connection",
)

export const createSurveyForm = async ({ categoryId }) => post(
  "/api/v1/survey-forms",
  { form: { category_id: categoryId } },
)

export const createSurveyConfiguration = async ({ surveyConfiguration }) => post(
  "/api/v1/surveys/survey-configurations",
  { survey_configuration: surveyConfiguration },
)

export const updateSurveyConfiguration = async ({ surveyConfigurationId, surveyConfiguration }) => put(
  `/api/v1/surveys/survey-configurations/${surveyConfigurationId}`,
  { survey_configuration: surveyConfiguration },
)

export const activateSurveyConfiguration = async (surveyConfigurationId) => post(
  `/api/v1/surveys/survey-configurations/${surveyConfigurationId}/activate`,
)

export const deactivateSurveyConfiguration = async (surveyConfigurationId) => post(
  `/api/v1/surveys/survey-configurations/${surveyConfigurationId}/deactivate`,
)

export const formSurveyBatches = async ({ formSlug }) => get(
  `/api/v1/surveys/forms/${formSlug}/survey-batches`,
)

export const formSurveyConfigurations = async ({ formSlug }) => get(
  `/api/v1/surveys/forms/${formSlug}/survey-configurations`,
)

export const createSurveyBatch = async ({ surveyConfigurationId }) => post(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/survey-batches`,
)

export const createSurveyToken = async ({ surveyBatchId, surveyTokenWithContact }) => post(
  `api/v1/surveys/survey-batches/${surveyBatchId}/survey-tokens`,
  { survey_token: surveyTokenWithContact },
)

export const destroySurveyBatchContact = async ({ surveyBatchId, surveyTokenId }) => destroy(
  `api/v1/surveys/survey-batches/${surveyBatchId}/survey-tokens/${surveyTokenId}`,
)

export const getSurveyRecipients = async (page = 1) => get("/api/v1/surveys/survey-recipients", { page })

export const updateSurveyBatch = async ({ surveyConfigurationId, surveyBatchId, surveyBatch }) => put(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/survey-batches/${surveyBatchId}`,
  { survey_batch: surveyBatch },
)

export const getSurveyConfigurationContacts = async ({ surveyConfigurationId }) => get(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/contact-inclusions`,
)

export const createSurveyConfigurationContact = async ({ surveyConfigurationId, contact }) => post(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/contact-inclusions`,
  contact,
)

export const destroySurveyConfigurationContact = async ({ surveyConfigurationId, contactInclusionId }) => destroy(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/contact-inclusions/${contactInclusionId}`,
)

export const createSurveyConfigurationContactsUpload = ({ surveyConfigurationId, formData }) => postFiles(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/contacts-uploads`,
  formData,
)

export const surveyConfigurationContactsUpload = ({ surveyConfigurationId, contactsUploadId }) => get(
  `api/v1/surveys/survey-configurations/${surveyConfigurationId}/contacts-uploads/${contactsUploadId}`,
)

export const formSubmissionSurveyTokenPublicDetails = ({ formSubmissionSlug }) => get(
  `api/v1/surveys/form-submissions/${formSubmissionSlug}/survey-token-public-details`,
)

export const saveForm = async ({ form }) => post(
  `/api/v1/forms/${form.slug}/current-version`,
  { form },
)

export const updateFormIcon = async ({ formSlug, icon, iconColor }) => put(
  `/api/v1/forms/${formSlug}/icon`,
  {
    icon,
    icon_color: iconColor,
  },
)

export const updateFormTooltipDescription = async ({ formSlug, tooltipDescription }) => put(
  `/api/v1/forms/${formSlug}/tooltip-description`,
  { tooltip_description: tooltipDescription },
)

export const updateFormAcceptsFiles = async ({ formSlug, acceptsFiles }) => put(
  `/api/v1/forms/${formSlug}/accepts-files`,
  { accepts_submitted_files: acceptsFiles },
)

export const updateFormRequiresAuthentication = async ({ formSlug, requiresAuthentication }) => put(
  `/api/v1/forms/${formSlug}/requires-authentication`,
  { requires_authentication: requiresAuthentication },
)

export const updateFormTracksSubmissions = async ({ formSlug, tracksSubmissions }) => put(
  `/api/v1/forms/${formSlug}/tracks-submissions`,
  { tracks_submissions: tracksSubmissions },
)

export const updateFormActive = async ({ formSlug, active }) => put(
  `/api/v1/forms/${formSlug}/active`,
  { active },
)

export const updateFormListed = async ({ formSlug, listed }) => put(
  `/api/v1/forms/${formSlug}/listed`,
  { listed },
)

export const updateFormPublicSubmissionStatus = async ({ formSlug, publicSubmissionStatus }) => put(
  `/api/v1/forms/${formSlug}/public-submission-status`,
  { public_submission_status: publicSubmissionStatus },
)

export const updateFormDisplayCustomTextOnSuccess = async ({ formSlug, displayCustomTextOnSuccess }) => put(
  `/api/v1/forms/${formSlug}/display-custom-text-on-success`,
  { display_custom_text_on_success: displayCustomTextOnSuccess },
)

export const updateFormCustomSuccessText = async ({ formSlug, customSuccessText }) => put(
  `/api/v1/forms/${formSlug}/custom-success-text`,
  { custom_success_text: customSuccessText },
)

export const formQuestionHistory = async ({ formSlug }) => get(
  `/api/v1/forms/${formSlug}/question-history`,
)

export const formCard = ({ formSlug }) => (
  get(`/api/v1/forms/${formSlug}`)
)

export const getFormWorkflows = async ({ formSlug }) => get(
  `/api/v1/forms/${formSlug}/workflows`,
)

export const createFormWorkflow = async ({ formSlug, workflow }) => post(
  `/api/v1/forms/${formSlug}/workflows`,
  { workflow },
)

export const activateWorkflow = async ({ formSlug, workflowId }) => post(
  `/api/v1/forms/${formSlug}/workflows/${workflowId}/activate`,
)

export const deactivateWorkflow = async ({ formSlug, workflowId }) => post(
  `/api/v1/forms/${formSlug}/workflows/${workflowId}/deactivate`,
)

export const updateWorkflow = async ({ formSlug, workflowId, workflow }) => put(
  `/api/v1/forms/${formSlug}/workflows/${workflowId}`,
  { workflow },
)

export const deleteWorkflow = async ({ formSlug, workflowId }) => destroy(
  `/api/v1/forms/${formSlug}/workflows/${workflowId}`,
)

export const cachedQueryParams = async (key) => get(
  `/api/v1/public/cached-query-params/${key}`,
)

export const createFormSubmission = async ({
  answers, formLoadedAt, slug, submitterId,
}) => post(
  `/api/v1/public/forms/${slug}/form-submissions`,
  {
    form_submission: {
      answers,
      form_loaded_at: formLoadedAt,
    },
    submitter_id: submitterId,
  },
)

export const createSurveyFormSubmission = async ({ answers, formLoadedAt, token }) => post(
  `/api/v1/public/surveys/${token}`,
  {
    form_submission: {
      answers,
      form_loaded_at: formLoadedAt,
    },
  },
)

export const deleteFormSubmission = async ({ slug }) => destroy(
  `/api/v1/form-submissions/${slug}`,
)

export const recoverFormSubmission = async ({ slug }) => post(
  `/api/v1/form-submissions/${slug}/recover`,
)

export const updateFormSubmissionAnswers = async ({ answers, formVersionId, slug }) => put(
  `/api/v1/form-submissions/${slug}`,
  {
    form_submission: {
      answers,
      form_version_id: formVersionId,
    },
  },
)

export const attachFormSubmissionFiles = async ({
  signedBlobId, formSubmissionSlug, publicIdentifier,
}) => post(
  `/api/v1/public/form-submissions/${formSubmissionSlug}/submitted-files?public_identifier=${publicIdentifier}`,
  {
    form_submission: { signed_blob_id: signedBlobId },
  },
)

export const removeFormSubmissionFile = async ({
  signedBlobId, formSubmissionSlug, publicIdentifier,
}) => post(
  `/api/v1/public/form-submissions/${formSubmissionSlug}/submitted-files/destroy?public_identifier=${publicIdentifier}`,
  {
    form_submission: { signed_blob_id: signedBlobId },
  },
)

export const changeFormSubmissionFormType = async ({ formSubmissionSlug, newAnswers, newFormSlug }) => put(
  `/api/v1/form-submissions/${formSubmissionSlug}/form-type`,
  {
    form_submission: {
      new_answers: newAnswers,
      new_form_slug: newFormSlug,
    },
  },
)

export const exportFormSubmissionData = async ({
  slug, includeFormSubmission, includeConversations, includeResolution, includedTaskIds,
}) => post(
  `/api/v1/form-submissions/${slug}/export`,
  {
    include_form_submission: includeFormSubmission,
    include_conversations: includeConversations,
    include_resolution: includeResolution,
    included_task_ids: includedTaskIds,
  },
)

export const deleteFormSubmissionReport = async ({ id }) => destroy(
  `/api/v1/form-submission-reports/${id}`,
)

export const createFormSubmissionReport = async ({ report }) => post(
  "/api/v1/form-submission-reports",
  { report },
)

export const updateFormSubmissionReport = async ({ id, report }) => put(
  `/api/v1/form-submission-reports/${id}`,
  { report },
)

export const createSolutionLevelReport = async ({ report }) => post(
  "/api/v1/solution-level-reports",
  { report },
)

export const deleteSolutionLevelReport = async ({ id }) => destroy(
  `/api/v1/solution-level-reports/${id}`,
)

export const updateSolutionLevelReport = async ({ id, report }) => put(
  `/api/v1/solution-level-reports/${id}`,
  { report },
)

export const createSurveyReport = async ({ report }) => post(
  "/api/v1/surveys/survey-reports",
  { report },
)

export const updateSurveyReport = async ({ id, report }) => put(
  `/api/v1/surveys/survey-reports/${id}`,
  { report },
)

export const deleteSurveyReport = async ({ id }) => destroy(
  `/api/v1/surveys/survey-reports/${id}`,
)

export const deleteTaskReport = async ({ id }) => destroy(
  `/api/v1/task-reports/${id}`,
)

export const updateStatuses = async ({ categorySlug, statuses, statusesToDiscard }) => put(
  `/api/v1/categories/${categorySlug}/status-list`,
  {
    status_list: {
      discardable_statuses: statusesToDiscard.map(({ id, name }) => ({ id, name })),
      submission_statuses_attributes: statuses.map(({ id, name }) => ({ id, name })),
    },
  },
)

export const getTasks = async (page = 1) => get("/api/v1/tasks", { page })

export const unassignedTasks = async () => get("api/v1/unassigned-tasks")

export const closeTask = async ({ taskId }) => post(
  `/api/v1/tasks/${taskId}/close`,
)

export const reopenTask = async ({ taskId }) => post(
  `/api/v1/tasks/${taskId}/reopen`,
)

export const viewTask = async ({ taskId }) => post(
  `/api/v1/tasks/${taskId}/view`,
)

export const deleteTask = async ({ taskId }) => destroy(
  `/api/v1/tasks/${taskId}`,
)

export const recoverTask = async ({ taskId }) => post(
  `/api/v1/tasks/${taskId}/recover`,
)

export const statusList = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/status-list`,
)

export const createTask = async ({ formSubmissionSlug, model }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/tasks`,
  model,
)

export const updateTask = async ({ formSubmissionSlug, taskData, taskId }) => put(
  `/api/v1/form-submissions/${formSubmissionSlug}/tasks/${taskId}`,
  taskData,
)

export const taskAnswer = async ({ taskId, questionUuid }) => get(
  `/api/v1/tasks/${taskId}/answers/${questionUuid}`,
)

export const updateTaskAnswer = async ({ taskId, answer }) => put(
  `/api/v1/tasks/${taskId}/answer`,
  { answer },
)

export const deleteTaskAnswer = async ({ taskId, answerData }) => destroy(
  `/api/v1/tasks/${taskId}/answer`,
  answerData,
)

export const beginAnswerEditingSession = async ({ taskId, questionUuid }) => post(
  `/api/v1/tasks/${taskId}/answers/${questionUuid}/editor`,
)

export const endAnswerEditingSession = async ({ taskId, questionUuid }) => destroy(
  `/api/v1/tasks/${taskId}/answers/${questionUuid}/editor`,
)

export const taskAnswerEditingSessions = async ({ taskId }) => get(
  `/api/v1/tasks/${taskId}/answer-editing-sessions`,
)

export const formSubmissionTasks = async ({ formSubmissionSlug }) => get(
  `api/v1/form-submissions/${formSubmissionSlug}/tasks`,
)

export const formSubmissionAttachments = async ({ formSubmissionSlug }) => get(
  `api/v1/form-submissions/${formSubmissionSlug}/attachments`,
)

export const createAttachment = async ({ formSubmissionSlug, blobSignedId }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/attachments`,
  { active_storage_blob_signed_id: blobSignedId },
)

export const deleteAttachment = async ({ formSubmissionSlug, attachmentId }) => destroy(
  `/api/v1/form-submissions/${formSubmissionSlug}/attachments/${attachmentId}`,
)

export const headAttachmentUrl = async ({ formSubmissionSlug, attachmentId }) => head(
  `/api/v1/form-submissions/${formSubmissionSlug}/attachments/${attachmentId}/presigned-url`,
)

export const deleteSubmittedFile = async ({ formSubmissionSlug, attachmentId }) => destroy(
  `/api/v1/form-submissions/${formSubmissionSlug}/submitted-files/${attachmentId}`,
)

export const relatedFormSubmissions = async ({ formSubmissionSlug }) => get(
  `api/v1/form-submissions/${formSubmissionSlug}/related-form-submissions`,
)

export const searchForRelatedFormSubmissionOptions = async ({ formSubmissionId, query }) => post(
  "/api/v1/related-form-submission-options/search",
  { form_submission_id: formSubmissionId, query },
)

export const createFormSubmissionRelationship = async ({ formSubmissionSlug, relatedFormSubmissionId }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/related-form-submissions`,
  { related_form_submission_id: relatedFormSubmissionId },
)

export const deleteFormSubmissionRelationship = async ({ formSubmissionSlug, relatedFormSubmissionId }) => destroy(
  `/api/v1/form-submissions/${formSubmissionSlug}/related-form-submissions/${relatedFormSubmissionId}`,
)

export const formSubmissionConversations = async ({ formSubmissionSlug }) => get(
  `/api/v1/form-submissions/${formSubmissionSlug}/conversations`,
)

export const createFormSubmissionConversation = async ({ formSubmissionSlug, title, messageText }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/conversations`,
  {
    message_text: messageText,
    title,
  },
)

export const closeFormSubmissionConversation = async ({ conversationId, formSubmissionSlug }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/conversations/${conversationId}/close`,
)

export const reopenFormSubmissionConversation = async ({ conversationId, formSubmissionSlug }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/conversations/${conversationId}/reopen`,
)

export const createConversationMessage = async ({ conversationId, formSubmissionSlug, text }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/conversations/${conversationId}/messages`,
  { text },
)

export const publicFormSubmissionConversations = async ({ formSubmissionSlug }) => get(
  `/api/v1/public/form-submissions/${formSubmissionSlug}/conversations`,
)

export const publicCreateConversationMessage = async ({ conversationId, formSubmissionSlug, text }) => post(
  `/api/v1/public/form-submissions/${formSubmissionSlug}/conversations/${conversationId}/messages`,
  { text },
)

export const publicFormSubmissionAnswers = async ({ formSubmissionSlug }) => get(
  `api/v1/public/form-submissions/${formSubmissionSlug}/answers`,
)

export const formSubmissionResolution = async ({ formSubmissionSlug }) => get(
  `/api/v1/form-submissions/${formSubmissionSlug}/resolution`,
)

export const createFormSubmissionResolution = async ({ formSubmissionSlug, text }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/resolution`,
  { text },
)

export const updateFormSubmissionResolution = async ({ formSubmissionSlug, text }) => put(
  `/api/v1/form-submissions/${formSubmissionSlug}/resolution`,
  { text },
)

export const deleteFormSubmissionResolution = async ({ formSubmissionSlug }) => destroy(
  `/api/v1/form-submissions/${formSubmissionSlug}/resolution`,
)

export const getUsers = async (params) => get("/api/v1/users", params)

export const createUser = async ({ user }) => post("/api/v1/users", { user })

export const updateUser = async ({ userId, user }) => put(`/api/v1/users/${userId}`, { user })

export const updateUserRole = async ({ userId, role }) => put(`/api/v1/users/${userId}/role`, { user: { role } })

export const updateUserLocked = async ({ userId, locked }) => put(`/api/v1/users/${userId}`, { user: { locked } })

export const activateUserAccount = async ({ userId }) => post(`/api/v1/users/${userId}/activate`)

export const deactivateUserAccount = async ({ userId }) => post(`/api/v1/users/${userId}/deactivate`)

export const removeOTPConfirmation = async ({ userId }) => destroy(`/api/v1/users/${userId}/otp-confirmation`)

export const activityHubOrganizationUsers = async (params) => get("api/v1/activity-hub-organization-users", params)

export const getGroups = async () => get("/api/v1/groups")

export const getDepartments = async () => get("/api/v1/departments")

export const activateDepartment = async (departmentId) => post(`/api/v1/departments/${departmentId}/activate`)

export const deactivateDepartment = async (departmentId) => post(`/api/v1/departments/${departmentId}/deactivate`)

export const getCategory = async ({ categorySlug }) => get(`api/v1/categories/${categorySlug}`)

export const getCategoryActiveSubformOptions = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/active-subform-options`,
)

export const getCategorySubforms = async ({ categorySlug }) => get(`/api/v1/categories/${categorySlug}/subforms`)

export const getCategoryStatuses = async ({ categorySlug }) => get(`/api/v1/categories/${categorySlug}/statuses`)

export const getFormSubmissionAccessOverview = ({ formSubmissionSlug }) => get(
  `/api/v1/form-submissions/${formSubmissionSlug}/access-overview`,
)

export const getFormSubmissionAccessGrantList = ({ formSubmissionSlug }) => get(
  `/api/v1/form-submissions/${formSubmissionSlug}/access-level-grants`,
)

export const createFormSubmissionAccessGrant = async ({ formSubmissionSlug, access_level_grant }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/access-level-grants`,
  { access_level_grant },
)

export const manageFormSubmissionAccessGrant = async ({ formSubmissionSlug, accessLevelGrant }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/access-level-grants/manage`,
  { access_level_grant: accessLevelGrant },
)

export const deleteFormSubmissionAccessGrant = async ({ formSubmissionSlug, grantId }) => destroy(
  `/api/v1/form-submissions/${formSubmissionSlug}/access-level-grants/${grantId}`,
)

export const getFormSubmissionAudits = async ({ formSubmissionSlug, page }) => get(
  `/api/v1/form-submissions/${formSubmissionSlug}/audits?page=${page}`,
)

export const getUserActionAudits = async ({ userId, cursor }) => {
  const path = `/api/v1/users/${userId}/audits`

  return get(path, { cursor })
}

export const userDiscardRelatedAudits = async ({ userId }) => get(
  `/api/v1/users/${userId}/discard-related-audits`,
)

export const userDepartmentMemberships = async ({ userId }) => get(
  `api/v1/users/${userId}/department-memberships`,
)

export const updateSubmissionStatus = async ({ formSubmissionSlug, statusName }) => put(
  `/api/v1/form-submissions/${formSubmissionSlug}/status`,
  { status_name: statusName },
)

export const closeSubmission = async ({ formSubmissionSlug }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/close`,
)

export const reopenSubmission = async ({ formSubmissionSlug }) => post(
  `/api/v1/form-submissions/${formSubmissionSlug}/reopen`,
)

export const getFormSubmissionStatusList = async ({ formSubmissionSlug }) => get(
  `/api/v1/form-submissions/${formSubmissionSlug}/statuses`,
)

export const getFormAccessGrantList = async ({ formSlug }) => get(
  `/api/v1/forms/${formSlug}/access-level-grants`,
)

export const getFormQuestions = async ({ formSlug, type }) => get(
  `/api/v1/forms/${formSlug}/questions`,
  type ? { type } : {},
)

export const getFormBuildersList = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/abilities/form-builders`,
)

export const getPossibleFormBuilderUsers = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/abilities/possible-form-builder-users`,
)

export const manageFormAccessGrant = async ({ formSlug, access_level_grant }) => post(
  `/api/v1/forms/${formSlug}/access-level-grants/manage`,
  { access_level_grant },
)

export const deleteFormAccessGrant = async ({ formSlug, grantId }) => destroy(
  `/api/v1/forms/${formSlug}/access-level-grants/${grantId}`,
)

export const categories = async () => get("api/v1/categories")

export const createCategory = async ({ category }) => post(
  "/api/v1/categories",
  { category },
)

export const updateCategory = async ({ categorySlug, category }) => put(
  `/api/v1/categories/${categorySlug}`,
  { category },
)

export const resetCategory = async ({ categoryId, subdomain, otp }) => post(
  `/api/v1/organizations/${subdomain}/category-resets`,
  { category_id: categoryId, otp },
)

export const getCategoryResetStatus = async ({ subdomain, categoryResetId }) => get(
  `/api/v1/organizations/${subdomain}/category-resets/${categoryResetId}`,
)

export const getBuildableCategories = async () => get("/api/v1/buildable-categories")

export const createCopyFormWithinOrganizationRequest = async ({ baseFormSlug, targetCategoryId }) => post(
  "/api/v1/copy-form-within-organization-requests",
  { base_form_slug: baseFormSlug, target_category_id: targetCategoryId },
)

export const copyFormWithinOrganizationRequest = ({ requestId }) => (
  get(`/api/v1/copy-form-within-organization-requests/${requestId}`)
)

export const getCategoryAccessGrantList = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/access-level-grants`,
)

export const createCategoryAccessGrant = async ({ categorySlug, access_level_grant }) => post(
  `/api/v1/categories/${categorySlug}/access-level-grants`,
  { access_level_grant },
)

export const updateCategoryAccessGrant = async ({ categorySlug, access_level_grant, grantId }) => put(
  `/api/v1/categories/${categorySlug}/access-level-grants/${grantId}`,
  { access_level_grant },
)

export const deleteCategoryAccessGrant = async ({ categorySlug, grantId }) => destroy(
  `/api/v1/categories/${categorySlug}/access-level-grants/${grantId}`,
)

export const updateCategoryAccessLevelRequirements = async ({ categorySlug, category }) => post(
  `/api/v1/categories/${categorySlug}/update-access-level-requirements`,
  { category },
)

export const getCategoryReportableFieldsWithOptions = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/reportable-field-options`,
)

export const getCategoryReportableFields = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/reportable-fields`,
)

export const createCategoryReportableField = async ({ categorySlug, reportableField }) => post(
  `/api/v1/categories/${categorySlug}/reportable-fields`,
  { reportable_field: reportableField },
)

export const updateCategoryReportableField = async ({ categorySlug, reportableFieldId, reportableField }) => put(
  `/api/v1/categories/${categorySlug}/reportable-fields/${reportableFieldId}`,
  { reportable_field: reportableField },
)

export const deleteCategoryReportableField = async ({ categorySlug, reportableFieldId }) => destroy(
  `/api/v1/categories/${categorySlug}/reportable-fields/${reportableFieldId}`,
)

export const createFormBuilder = async ({ categorySlug, category_ability }) => post(
  `/api/v1/categories/${categorySlug}/abilities/create-form-builder`,
  { category_ability },
)

export const deleteCategoryAbility = async ({ categorySlug, abilityId }) => destroy(
  `/api/v1/categories/${categorySlug}/abilities/${abilityId}`,
)

export const getCategoryFormSubmissions = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/form-submissions`,
)

export const categoryClosedFormSubmissions = async ({ categorySlug }, page = 1) => get(
  `/api/v1/categories/${categorySlug}/closed-form-submissions`,
  { page },
)

export const categoryOpenFormSubmissions = async ({ categorySlug }, page = 1) => get(
  `/api/v1/categories/${categorySlug}/open-form-submissions`,
  { page },
)

export const categoryRecoverableFormSubmissions = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/recoverable-form-submissions`,
)

export const categoryDeletedFormSubmissions = async ({ categorySlug }, page = 1) => get(
  `/api/v1/categories/${categorySlug}/deleted-form-submissions`,
  { page, per_page: 100 },
)

export const closedFormSubmissionCounts = async () => get(
  "api/v1/closed-form-submission-counts",
)

export const openFormSubmissionCounts = async () => get(
  "api/v1/open-form-submission-counts",
)

export const recoverableFormSubmissionCounts = async () => get(
  "api/v1/recoverable-form-submission-counts",
)

export const getCategoryForms = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/forms`,
)

export const getCategoryFormsForSubmitting = async ({ categorySlug }) => get(
  `/api/v1/categories/${categorySlug}/forms-for-submitting`,
)

export const categoryPublicForms = async ({ categorySlug }) => get(
  `api/v1/categories/${categorySlug}/public-forms`,
)

export const categorySurveyForms = async ({ categorySlug }) => get(
  `/api/v1/surveys/categories/${categorySlug}/forms`,
)

export const currentFormVersion = async ({ formSlug }) => get(
  `api/v1/forms/${formSlug}/current-version`,
)

export const formVersion = async ({ formSlug, formVersionId }) => get(
  `api/v1/forms/${formSlug}/versions/${formVersionId}`,
)

export const formForFormSubmission = async ({ formSlug }) => get(
  `api/v1/forms/${formSlug}/for-form-submission`,
)

export const formQuestionUuidsIdentifyingTaskDepartments = ({ formSlug }) => get(
  `/api/v1/forms/${formSlug}/question-uuids-identifying-task-departments`,
)

export const updateFormsOrder = async ({ categorySlug, formsOrder }) => put(
  `/api/v1/categories/${categorySlug}/form-order`,
  { forms_order: formsOrder },
)

export const createGroup = async ({ group }) => post(
  "/api/v1/groups",
  { group },
)

export const updateGroup = async ({ groupId, group }) => put(
  `/api/v1/groups/${groupId}`,
  { group },
)

export const createGroupMembership = async ({ groupId, group_membership }) => post(
  `/api/v1/groups/${groupId}/members`,
  { group_membership },
)

export const deleteGroupMembership = async ({ groupId, memberId }) => destroy(
  `/api/v1/groups/${groupId}/members/${memberId}`,
)

export const deleteGroup = async ({ groupId }) => destroy(
  `/api/v1/groups/${groupId}`,
)

export const getFacilities = async () => get("/api/v1/facilities")

export const getFacilityAudits = async ({ facilityId, page }) => get(
  `/api/v1/facilities/${facilityId}/audits?page=${page}`,
)

export const createFacility = async ({ facility }) => post(
  "/api/v1/facilities",
  { facility },
)

export const updateFacility = async ({ facilityId, facility }) => put(
  `/api/v1/facilities/${facilityId}`,
  { facility },
)

export const activateFacility = async ({ facilityId }) => post(`/api/v1/facilities/${facilityId}/activate`)

export const deactivateFacility = async ({ facilityId }) => post(`/api/v1/facilities/${facilityId}/deactivate`)

export const createFacilityDepartment = async ({ facilityId, department }) => post(
  `/api/v1/facilities/${facilityId}/departments`,
  { department },
)

export const updateFacilityDepartment = async ({ facilityId, departmentId, department }) => put(
  `/api/v1/facilities/${facilityId}/departments/${departmentId}`,
  { department },
)

export const createDepartmentMembership = async ({ facilityId, departmentId, departmentMembership }) => post(
  `/api/v1/facilities/${facilityId}/departments/${departmentId}/members`,
  {
    department_membership: {
      member_id: departmentMembership.memberId,
      segment: departmentMembership.segmentName,
    },
  },
)

export const removeUserFromDepartment = async ({ facilityId, departmentId, memberId }) => destroy(
  `/api/v1/facilities/${facilityId}/departments/${departmentId}/members/${memberId}`,
)

export const removeUserFromDepartmentSegment = async ({
  facilityId, departmentId, memberId, segmentName,
}) => destroy(
  `/api/v1/facilities/${facilityId}/departments/${departmentId}/segments/${segmentName}/members/${memberId}`,
)

export const updateDepartmentMemberSegment = async ({
  facilityId, departmentId, memberId, fromSegment, toSegment,
}) => put(
  `/api/v1/facilities/${facilityId}/departments/${departmentId}/segments/${fromSegment}/members/${memberId}`,
  { to_segment: toSegment },
)

export const updateOrganizationColor = async ({ color }) => put(
  "api/v1/color",
  { color },
)

export const updateOrganizationShortName = async ({ shortName }) => put(
  "api/v1/short-name",
  { short_name: shortName },
)

export const updateOrganizationClockFormat = async ({ clockFormat }) => put(
  "api/v1/clock-format",
  { clock_format: clockFormat },
)

export const updateTimezoneSetting = async ({ timezone }) => put(
  "/api/v1/timezone-setting",
  { timezone },
)

export const updateTaskReminders = async ({ task_reminders }) => put(
  "/api/v1/task-reminders",
  { task_reminders },
)

export const enableNotificationSetting = async ({ settingType }) => post(
  `/api/v1/notification-settings/${settingType}/enable`,
)

export const disableNotificationSetting = async ({ settingType }) => post(
  `/api/v1/notification-settings/${settingType}/disable`,
)

export const tasksAndSubmissionsSearch = async ({ query, pageSize }) => post(
  "/api/v1/tasks-and-submissions/search",
  { query, page_size: pageSize },
)

export const questionTags = async () => get(
  "/api/v1/question-tags",
)

export const createQuestionTag = async ({ questionTag }) => post(
  "/api/v1/question-tags",
  { question_tag: questionTag },
)

export const facilityGroupCodes = async () => get(
  "/api/v1/facility-group-codes",
)

export const createFacilityGroupCode = async ({ facilityGroupCode }) => post(
  "/api/v1/facility-group-codes",
  { facility_group_code: facilityGroupCode },
)

export const getFacilityGroupCodeAssignments = async ({ facilityId }) => get(
  `/api/v1/facilities/${facilityId}/facility-group-code-assignments`,
)

export const updateFacilityGroupCodeAssignments = async ({ facilityId, facilityGroupCodeIds }) => put(
  `/api/v1/facilities/${facilityId}/facility-group-code-assignments`,
  { facility_group_code_ids: facilityGroupCodeIds },
)

export const createReadReceipt = async ({ readableType, readableId }) => post(
  "/api/v1/public/read-receipts",
  { readable_type: readableType, readable_id: readableId },
)

export const startImpersonation = async ({ impersonateeId }) => post(
  "/api/v1/impersonation",
  { user_id: impersonateeId },
)

export const endImpersonation = async () => destroy(
  "/api/v1/impersonation",
)

export const createFormSubmissionReportReportExport = ({
  dateAnswerFilters,
  earliestDate: earliestSubmissionDate,
  latestDate: latestSubmissionDate,
  locationAnswerFilters,
  reportFileType,
  reportId,
  selectAnswerFilters,
}) => post(
  `/api/v1/form-submission-reports/${reportId}/exports`,
  {
    form_submission_report_export: {
      date_answer_filters: dateAnswerFilters.map(({ earliestDate, latestDate, questionUuid }) => ({
        earliest_date: earliestDate,
        latest_date: latestDate,
        question_uuid: questionUuid,
      })),
      earliest_date: earliestSubmissionDate,
      latest_date: latestSubmissionDate,
      location_answer_filters: locationAnswerFilters.map(({ departmentIds, questionUuid }) => ({
        department_ids: departmentIds,
        question_uuid: questionUuid,
      })),
      select_answer_filters: selectAnswerFilters.map(({ questionUuid, selectedAnswers }) => ({
        question_uuid: questionUuid,
        selected_answers: selectedAnswers,
      })),
      export_file_type: reportFileType,
    },
  },
)

export const createSolutionLevelReportReportExport = ({
  earliestDate: earliestSubmissionDate,
  latestDate: latestSubmissionDate,
  reportableFieldDateAnswerFilters,
  reportableFieldLocationAnswerFilters,
  reportableFieldSelectAnswerFilters,
  reportFileType,
  reportId,
}) => post(
  `/api/v1/solution-level-reports/${reportId}/exports`,
  {
    solution_level_report_export: {
      earliest_date: earliestSubmissionDate,
      latest_date: latestSubmissionDate,
      reportable_field_date_answer_filters: reportableFieldDateAnswerFilters.map(
        ({ earliestDate, latestDate, reportableFieldId }) => ({
          earliest_date: earliestDate,
          latest_date: latestDate,
          reportable_field_id: reportableFieldId,
        }),
      ),
      reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(
        ({ departmentIds, reportableFieldId }) => ({
          department_ids: departmentIds,
          reportable_field_id: reportableFieldId,
        }),
      ),
      reportable_field_select_answer_filters: reportableFieldSelectAnswerFilters.map(
        ({ reportableFieldId, selectedAnswers }) => ({
          reportable_field_id: reportableFieldId,
          selected_answers: selectedAnswers,
        }),
      ),
      export_file_type: reportFileType,
    },
  },
)

export const createTaskReportExport = ({
  earliestClosedDate,
  earliestDueDate,
  includeClosedTasks,
  includedFormSlugs,
  includeOpenTasks,
  latestClosedDate,
  latestDueDate,
  reportFileType,
  reportId,
}) => post(
  `/api/v1/task-reports/${reportId}/exports`,
  {
    task_report_export: {
      earliest_closed_date: earliestClosedDate,
      earliest_due_date: earliestDueDate,
      export_file_type: reportFileType,
      include_closed_tasks: includeClosedTasks,
      include_open_tasks: includeOpenTasks,
      included_form_slugs: includedFormSlugs,
      latest_closed_date: latestClosedDate,
      latest_due_date: latestDueDate,
    },
  },
)

export const createFormSubmissionScheduledReportExport = ({
  activeStatus,
  dateAnswerFilters,
  frequency,
  locationAnswerFilters,
  selectAnswerFilters,
  recurrenceDayOfMonth,
  recurrenceDayOfWeek,
  scheduledReportExportName,
  startDate,
  submissionPreviousRangeLength,
  submissionPreviousRangeUnit,
  submissionTimestampFilter,
  reportId,
}) => post(
  `/api/v1/form-submission-reports/${reportId}/scheduled-report-exports`,
  {
    form_submission_scheduled_report_export: {
      name: scheduledReportExportName,
      form_submission_previous_range_length: submissionPreviousRangeLength,
      form_submission_previous_range_unit: submissionPreviousRangeUnit,
      form_submission_timestamp_filter: submissionTimestampFilter,
      date_answer_filters: dateAnswerFilters,
      location_answer_filters: locationAnswerFilters.map(({ departmentIds, questionUuid }) => ({
        department_ids: departmentIds,
        question_uuid: questionUuid,
      })),
      select_answer_filters: selectAnswerFilters.map(({ questionUuid, selectedAnswers }) => ({
        question_uuid: questionUuid,
        selected_answers: selectedAnswers,
      })),
      scheduled_report_export_schedule_attributes: {
        frequency,
        active: activeStatus,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const updateFormSubmissionScheduledReportExport = ({
  activeStatus,
  dateAnswerFilters,
  locationAnswerFilters,
  selectAnswerFilters,
  recurrenceDayOfMonth,
  recurrenceDayOfWeek,
  scheduledReportExportName,
  startDate,
  submissionPreviousRangeLength,
  submissionPreviousRangeUnit,
  submissionTimestampFilter,
  reportId,
  scheduledReportExportId,
}) => put(
  `/api/v1/form-submission-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
  {
    form_submission_scheduled_report_export: {
      name: scheduledReportExportName,
      form_submission_previous_range_length: submissionPreviousRangeLength,
      form_submission_previous_range_unit: submissionPreviousRangeUnit,
      form_submission_timestamp_filter: submissionTimestampFilter,
      date_answer_filters: dateAnswerFilters,
      location_answer_filters: locationAnswerFilters.map(({ departmentIds, questionUuid }) => ({
        department_ids: departmentIds,
        question_uuid: questionUuid,
      })),
      select_answer_filters: selectAnswerFilters.map(({ questionUuid, selectedAnswers }) => ({
        question_uuid: questionUuid,
        selected_answers: selectedAnswers,
      })),
      scheduled_report_export_schedule_attributes: {
        active: activeStatus,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const destroyFormSubmissionScheduledReportExport = ({ reportId, scheduledReportExportId }) => destroy(
  `/api/v1/form-submission-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
)

export const createSolutionLevelScheduledReportExport = ({
  activeStatus,
  frequency,
  recurrenceDayOfMonth,
  recurrenceDayOfWeek,
  reportableFieldDateAnswerFilters,
  reportableFieldLocationAnswerFilters,
  reportableFieldSelectAnswerFilters,
  scheduledReportExportName,
  startDate,
  submissionPreviousRangeLength,
  submissionPreviousRangeUnit,
  submissionTimestampFilter,
  reportId,
}) => post(
  `/api/v1/solution-level-reports/${reportId}/scheduled-report-exports`,
  {
    solution_level_scheduled_report_export: {
      name: scheduledReportExportName,
      form_submission_previous_range_length: submissionPreviousRangeLength,
      form_submission_previous_range_unit: submissionPreviousRangeUnit,
      form_submission_timestamp_filter: submissionTimestampFilter,
      reportable_field_date_answer_filters: reportableFieldDateAnswerFilters,
      reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(({ departmentIds, reportableFieldId }) => ({
        department_ids: departmentIds,
        reportable_field_id: reportableFieldId,
      })),
      reportable_field_select_answer_filters: reportableFieldSelectAnswerFilters.map(({ reportableFieldId, selectedAnswers }) => ({
        reportable_field_id: reportableFieldId,
        selected_answers: selectedAnswers,
      })),
      scheduled_report_export_schedule_attributes: {
        active: activeStatus,
        frequency,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const updateSolutionLevelScheduledReportExport = ({
  activeStatus,
  recurrenceDayOfMonth,
  recurrenceDayOfWeek,
  reportableFieldDateAnswerFilters,
  reportableFieldLocationAnswerFilters,
  reportableFieldSelectAnswerFilters,
  scheduledReportExportName,
  startDate,
  submissionPreviousRangeLength,
  submissionPreviousRangeUnit,
  submissionTimestampFilter,
  reportId,
  scheduledReportExportId,
}) => put(
  `/api/v1/solution-level-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
  {
    solution_level_scheduled_report_export: {
      name: scheduledReportExportName,
      form_submission_previous_range_length: submissionPreviousRangeLength,
      form_submission_previous_range_unit: submissionPreviousRangeUnit,
      form_submission_timestamp_filter: submissionTimestampFilter,
      reportable_field_date_answer_filters: reportableFieldDateAnswerFilters,
      reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(({ departmentIds, reportableFieldId }) => ({
        department_ids: departmentIds,
        reportable_field_id: reportableFieldId,
      })),
      reportable_field_select_answer_filters: reportableFieldSelectAnswerFilters.map(({ reportableFieldId, selectedAnswers }) => ({
        reportable_field_id: reportableFieldId,
        selected_answers: selectedAnswers,
      })),
      scheduled_report_export_schedule_attributes: {
        active: activeStatus,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const destroySolutionLevelScheduledReportExport = ({ reportId, scheduledReportExportId }) => destroy(
  `/api/v1/solution-level-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
)

export const createSurveyScheduledReportExport = ({
  activeStatus,
  dateAnswerFilters,
  frequency,
  locationAnswerFilters,
  recurrenceDayOfWeek,
  recurrenceDayOfMonth,
  scheduledReportExportName,
  startDate,
  surveyFilters,
  reportId,
}) => post(
  `/api/v1/surveys/survey-reports/${reportId}/scheduled-report-exports`,
  {
    survey_scheduled_report_export: {
      name: scheduledReportExportName,
      date_answer_filters: dateAnswerFilters,
      location_answer_filters: locationAnswerFilters.map(({ departmentIds, questionUuid }) => ({
        department_ids: departmentIds,
        question_uuid: questionUuid,
      })),
      survey_filters: surveyFilters,
      scheduled_report_export_schedule_attributes: {
        active: activeStatus,
        frequency,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const updateSurveyScheduledReportExport = ({
  activeStatus,
  dateAnswerFilters,
  locationAnswerFilters,
  recurrenceDayOfWeek,
  recurrenceDayOfMonth,
  scheduledReportExportName,
  startDate,
  surveyFilters,
  reportId,
  scheduledReportExportId,
}) => put(
  `/api/v1/surveys/survey-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
  {
    survey_scheduled_report_export: {
      name: scheduledReportExportName,
      date_answer_filters: dateAnswerFilters,
      location_answer_filters: locationAnswerFilters.map(({ departmentIds, questionUuid }) => ({
        department_ids: departmentIds,
        question_uuid: questionUuid,
      })),
      survey_filters: surveyFilters,
      scheduled_report_export_schedule_attributes: {
        active: activeStatus,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const destroySurveyScheduledReportExport = ({ reportId, scheduledReportExportId }) => destroy(
  `/api/v1/surveys/survey-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
)

// The task filter is a single nested object rather than the lists the other report types send, since
// an export has one date window, one status selection and one form selection.
export const createScheduledTaskReportExport = ({
  activeStatus,
  frequency,
  includeClosedTasks,
  includedFormSlugs,
  includeOpenTasks,
  recurrenceDayOfMonth,
  recurrenceDayOfWeek,
  reportId,
  scheduledReportExportName,
  startDate,
  taskPreviousRangeLength,
  taskPreviousRangeUnit,
  taskTimestampFilter,
}) => post(
  `/api/v1/task-reports/${reportId}/scheduled-report-exports`,
  {
    scheduled_task_report_export: {
      name: scheduledReportExportName,
      task_filter: {
        timestamp_filter: taskTimestampFilter,
        previous_range_length: taskPreviousRangeLength,
        previous_range_unit: taskPreviousRangeUnit,
        include_open_tasks: includeOpenTasks,
        include_closed_tasks: includeClosedTasks,
        included_form_slugs: includedFormSlugs,
      },
      scheduled_report_export_schedule_attributes: {
        frequency,
        active: activeStatus,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

// Frequency is left out: it is fixed once a schedule exists, and decides how the window is snapped.
export const updateScheduledTaskReportExport = ({
  activeStatus,
  includeClosedTasks,
  includedFormSlugs,
  includeOpenTasks,
  recurrenceDayOfMonth,
  recurrenceDayOfWeek,
  reportId,
  scheduledReportExportId,
  scheduledReportExportName,
  startDate,
  taskPreviousRangeLength,
  taskPreviousRangeUnit,
  taskTimestampFilter,
}) => put(
  `/api/v1/task-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
  {
    scheduled_task_report_export: {
      name: scheduledReportExportName,
      task_filter: {
        timestamp_filter: taskTimestampFilter,
        previous_range_length: taskPreviousRangeLength,
        previous_range_unit: taskPreviousRangeUnit,
        include_open_tasks: includeOpenTasks,
        include_closed_tasks: includeClosedTasks,
        included_form_slugs: includedFormSlugs,
      },
      scheduled_report_export_schedule_attributes: {
        active: activeStatus,
        recurrence_day_of_month: recurrenceDayOfMonth,
        recurrence_day_of_week: recurrenceDayOfWeek,
        start_date: startDate,
      },
    },
  },
)

export const destroyScheduledTaskReportExport = ({ reportId, scheduledReportExportId }) => destroy(
  `/api/v1/task-reports/${reportId}/scheduled-report-exports/${scheduledReportExportId}`,
)

export const createSurveyReportExport = ({
  dateAnswerFilters,
  surveyBatchIds,
  locationAnswerFilters,
  reportFileType,
  reportId,
}) => post(
  `/api/v1/surveys/survey-reports/${reportId}/exports`,
  {
    survey_report_export: {
      survey_filters: [{ survey_batch_ids: surveyBatchIds }],
      date_answer_filters: dateAnswerFilters.map(({ earliestDate, latestDate, questionUuid }) => ({
        earliest_date: earliestDate,
        latest_date: latestDate,
        question_uuid: questionUuid,
      })),
      location_answer_filters: locationAnswerFilters.map(({ departmentIds, questionUuid }) => ({
        department_ids: departmentIds,
        question_uuid: questionUuid,
      })),
      export_file_type: reportFileType,
    },
  },
)

export const reportExport = ({ reportExportId }) => get(`/api/v1/report-exports/${reportExportId}`)

export const taskReportExport = ({ reportExportId }) => get(`/api/v1/task-report-exports/${reportExportId}`)

export const activateScheduledReportExportSchedule = ({ scheduleId }) => post(`/api/v1/report-export-schedules/${scheduleId}/activate`)
export const deactivateScheduledReportExportSchedule = ({ scheduleId }) => post(`/api/v1/report-export-schedules/${scheduleId}/deactivate`)

export const createMetabaseDashboardImageCapture = ({ dashboardUrl }) => post(
  "/api/v1/metabase-dashboard-image-captures",
  {
    metabase_dashboard_image_capture: {
      dashboard_url: dashboardUrl,
    },
  },
)

export const metabaseDashboardImageCapture = ({ imageCaptureId }) => get(
  `/api/v1/metabase-dashboard-image-captures/${imageCaptureId}`,
)

export const reportError = async ({ errorDetails }) => post(
  "/api/v1/public/frontend-errors/",
  {
    frontend_error: errorDetails,
  },
)

export const historicalAccessToolAddAccessPreviews = ({
  accessLevel,
  earliestSubmissionDate,
  formsForSubmittingIds,
  latestSubmissionDate,
  reportableFieldLocationAnswerFilters,
  taskFormIds,
  userId,
}) => (
  post(
    "/api/v1/historical-access-tool/add-access/previews",
    {
      instructions: {
        access_level: accessLevel,
        earliest_submission_date: earliestSubmissionDate,
        forms_for_submitting_ids: formsForSubmittingIds,
        latest_submission_date: latestSubmissionDate,
        reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(({ reportableFieldId, departmentIds }) => (
          {
            reportable_field_id: reportableFieldId,
            department_ids: departmentIds,
          }
        )),
        task_form_ids: taskFormIds,
        user_id: userId,
      },
    },
  )
)

export const historicalAccessToolAddAccess = ({
  accessLevel,
  earliestSubmissionDate,
  formsForSubmittingIds,
  latestSubmissionDate,
  reportableFieldLocationAnswerFilters,
  taskFormIds,
  userId,
}) => (
  post(
    "/api/v1/historical-access-tool/add-access",
    {
      instructions: {
        access_level: accessLevel,
        earliest_submission_date: earliestSubmissionDate,
        forms_for_submitting_ids: formsForSubmittingIds,
        latest_submission_date: latestSubmissionDate,
        reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(({ reportableFieldId, departmentIds }) => (
          {
            reportable_field_id: reportableFieldId,
            department_ids: departmentIds,
          }
        )),
        task_form_ids: taskFormIds,
        user_id: userId,
      },
    },
  )
)

export const historicalAccessToolRemoveAccessPreviews = ({
  earliestSubmissionDate,
  formsForSubmittingIds,
  latestSubmissionDate,
  reportableFieldLocationAnswerFilters,
  userId,
}) => (
  post(
    "/api/v1/historical-access-tool/remove-access/previews",
    {
      instructions: {
        earliest_submission_date: earliestSubmissionDate,
        forms_for_submitting_ids: formsForSubmittingIds,
        latest_submission_date: latestSubmissionDate,
        reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(({ reportableFieldId, departmentIds }) => (
          {
            reportable_field_id: reportableFieldId,
            department_ids: departmentIds,
          }
        )),
        user_id: userId,
      },
    },
  )
)

export const historicalAccessToolRemoveAccess = ({
  earliestSubmissionDate,
  formsForSubmittingIds,
  latestSubmissionDate,
  reportableFieldLocationAnswerFilters,
  userId,
}) => (
  post(
    "/api/v1/historical-access-tool/remove-access",
    {
      instructions: {
        earliest_submission_date: earliestSubmissionDate,
        forms_for_submitting_ids: formsForSubmittingIds,
        latest_submission_date: latestSubmissionDate,
        reportable_field_location_answer_filters: reportableFieldLocationAnswerFilters.map(({ reportableFieldId, departmentIds }) => (
          {
            reportable_field_id: reportableFieldId,
            department_ids: departmentIds,
          }
        )),
        user_id: userId,
      },
    },
  )
)

export const getHistoricalAccessToolAddAccessPreview = ({ previewId }) => (
  get(`/api/v1/historical-access-tool/add-access/previews/${previewId}`)
)

export const getHistoricalAccessToolRemoveAccessPreview = ({ previewId }) => (
  get(`/api/v1/historical-access-tool/remove-access/previews/${previewId}`)
)

export const getAnswerOptionsLists = async () => get("/api/v1/answer-options-lists")

export const getPublicAnswerOptionsLists = async ({ formSlug }) => get(
  `/api/v1/public/forms/${formSlug}/answer-options-lists`,
)

export const signOut = () => post(
  "/api/v1/sign-out",
)

// Endpoint is under the "php" subdomain
export const organizationAddOnFeatureSubscriptions = ({ organizationSubdomain }) => get(
  `api/v1/organizations/${organizationSubdomain}/add-on-feature-subscriptions`,
)

// Endpoint is under the "php" subdomain
export const subscribeOrganizationToAddOnFeature = ({ organizationSubdomain, addOnFeatureName }) => post(
  `/api/v1/organizations/${organizationSubdomain}/add-on-features/${addOnFeatureName}/subscribe`,
)

// Endpoint is under the "php" subdomain
export const unsubscribeOrganizationFromAddOnFeature = ({ organizationSubdomain, addOnFeatureName }) => destroy(
  `/api/v1/organizations/${organizationSubdomain}/add-on-features/${addOnFeatureName}/unsubscribe`,
)

// Endpoint is under the "php" subdomain
export const enableOrganizationAddOnFeatureConfiguration = ({ organizationSubdomain, addOnFeatureName, property }) => post(
  `/api/v1/organizations/${organizationSubdomain}/add-on-features/${addOnFeatureName}/configurations/${property}/enable`,
)

// Endpoint is under the "php" subdomain
export const disableOrganizationAddOnFeatureConfiguration = ({ organizationSubdomain, addOnFeatureName, property }) => post(
  `/api/v1/organizations/${organizationSubdomain}/add-on-features/${addOnFeatureName}/configurations/${property}/disable`,
)

// Endpoint is under the "php" subdomain
export const deletePhpAdminUser = ({ organizationSubdomain, userId }) => destroy(
  `/api/v1/organizations/${organizationSubdomain}/users/${userId}`,
)

// Endpoint is under the "php" subdomain
export const createOrganizationAccess = ({ organizationSubdomain, identityId }) => post(
  `/api/v1/organizations/${organizationSubdomain}/users/`,
  {
    identity_id: identityId,
  },
)

// Endpoint is under the "php" subdomain
export const createOrganizationWhitelistedUserEmailDescription = ({ organizationSubdomain, description }) => post(
  `/api/v1/organizations/${organizationSubdomain}/whitelisted-email-descriptions/`,
  {
    whitelisted_user_email_description: { description },
  },
)

// Endpoint is under the "php" subdomain
export const phpCategoryFormsForSubmitting = ({ categoryId, organizationSubdomain }) => get(
  `/api/v1/organizations/${organizationSubdomain}/categories/${categoryId}/forms-for-submitting`,
)

// Endpoint is under the "php" subdomain
export const phpCategoryReportableFields = ({ categoryId, organizationSubdomain }) => get(
  `/api/v1/organizations/${organizationSubdomain}/categories/${categoryId}/reportable-fields`,
)

// Endpoint is under the "php" subdomain
export const phpCategoryReportableFieldApplicableQuestionOptions = ({
  categoryId, formId, organizationSubdomain, reportableFieldId,
}) => get(
  `/api/v1/organizations/${organizationSubdomain}/categories/${categoryId}/reportable-fields/${reportableFieldId}/applicable-questions`,
  { form_id: formId },
)

// Endpoint is under the "php" subdomain
export const phpBackfillCategoryReportableField = ({
  categoryId, formId, organizationSubdomain, questionUuid, reportableFieldId,
}) => post(
  `/api/v1/organizations/${organizationSubdomain}/categories/${categoryId}/reportable-fields/${reportableFieldId}/backfill`,
  {
    form_id: formId,
    question_uuid: questionUuid,
  },
)

// Endpoint is under the "php" subdomain
export const phpOrganizationCustomNames = ({ organizationSubdomain }) => get(
  `/api/v1/organizations/${organizationSubdomain}/custom-names`,
)

// Endpoint is under the "php" subdomain
export const phpCreateOrganizationCustomName = ({ key, name, organizationSubdomain }) => post(
  `/api/v1/organizations/${organizationSubdomain}/custom-names/${key}`,
  { name },
)

// Endpoint is under the "php" subdomain
export const phpUpdateOrganizationCustomName = ({ key, name, organizationSubdomain }) => put(
  `/api/v1/organizations/${organizationSubdomain}/custom-names/${key}`,
  { name },
)

// Endpoint is under the "php" subdomain
export const phpDeleteOrganizationCustomName = ({ key, organizationSubdomain }) => destroy(
  `/api/v1/organizations/${organizationSubdomain}/custom-names/${key}`,
)

// Endpoint is under the "php" subdomain
export const phpOrganizationSAMLConfigurations = ({ organizationSubdomain }) => get(
  `/api/v1/organizations/${organizationSubdomain}/saml/configurations`,
)

// Endpoint is under the "php" subdomain
export const phpOrganizationSAMLConfigurationEnableSLO = ({ id, organizationSubdomain }) => post(
  `/api/v1/organizations/${organizationSubdomain}/saml/configurations/${id}/enable-single-logout`,
)

// Endpoint is under the "php" subdomain
export const phpOrganizationSAMLConfigurationDisableSLO = ({ id, organizationSubdomain }) => post(
  `/api/v1/organizations/${organizationSubdomain}/saml/configurations/${id}/disable-single-logout`,
)

// Endpoint is under the "php" subdomain
export const phpRenameOrganizationSAMLConfiguration = ({ id, name, organizationSubdomain }) => post(
  `/api/v1/organizations/${organizationSubdomain}/saml/configurations/${id}/rename`,
  { name },
)

// Endpoint is under the "php" subdomain
export const phpDeleteOrganizationSAMLConfiguration = ({ id, organizationSubdomain }) => destroy(
  `/api/v1/organizations/${organizationSubdomain}/saml/configurations/${id}`,
)

// Endpoint is under the "php" subdomain
export const phpCreateOrganizationSAMLConfiguration = ({ idpMetadataFile, name, organizationSubdomain }) => {
  const formData = new FormData()
  formData.append("idp_metadata_file", idpMetadataFile)
  formData.append("name", name)

  return post(
    `/api/v1/organizations/${organizationSubdomain}/saml/configurations`,
    formData,
  )
}

// Endpoint is under the "php" subdomain
export const phpOrganizationSAMLConfigurationUpdateIDPSettings = ({ id, idpMetadataFile, organizationSubdomain }) => {
  const formData = new FormData()
  formData.append("idp_metadata_file", idpMetadataFile)

  return post(
    `/api/v1/organizations/${organizationSubdomain}/saml/configurations/${id}/idp-settings`,
    formData,
  )
}
